import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import CustomCalendar from "./CustomCalendar";
import moment from 'moment'
import Button from "../elements/Button";
import AjaxLoaderImg from "./AjaxLoaderImg";
import Error from './Error';
import DataSource from "../DataSource";

const BookingCalendar = ({ availableDates, handleCancel, setIsSuccess, serviceDetails }) => {

    const [dateObj, setDateObj] = useState(null)
    const [selectedTime, setSelectedTime] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [availableSlots, setAvailableSlots] = useState([])
    const [error, setError] = useState({})

    useEffect(() => {
        setSlotsData()
    }, [])

    const setSlotsData = () => {
        let data = [];
        Object.keys(availableDates).forEach(date => {
            const slotObj = availableDates[date]
            data.push({
                date: date,
                times: [...formatTimes(slotObj.slots)]
            })
        })
        setAvailableSlots(data)
    }

    const formatTimes = slotsArr => {
        return slotsArr.map(date => {
            return {
                slotId: date.id,
                displayTime: moment(date.start_time).format('hh:mmA') + ' to ' + moment(date.end_time).format('hh:mmA')
            }
        })
    }

    const handleSetDate = date => {
        let filter = availableSlots.filter(item => item.date === moment(date).format('YYYY-MM-DD'))
        if (filter.length > 0) {
            setSelectedTime(filter[0].times.length > 0 ? filter[0].times[0] : null)
            setDateObj(filter[0])
        }
    }

    const handleConfirmAppointment = event => {
        event.preventDefault();
        setIsLoading(true)
        DataSource.postRescheduleAppointment(serviceDetails.serviceId, selectedTime.slotId)
            .then(response => {
                setIsLoading(false)
                setIsSuccess(true)
            })
            .catch(error => {
                if (error.message) {
                    setError(error.message)
                } else {
                    setError('Oops, something went wrong. Please try again.')
                }
                setIsLoading(false)
                setIsSuccess(false)
            })
    }

    const Help = () => {
        return (
            <p className="m-0">Please contact our support team if you're still having problems. <a href="tel:133938" className="bold underline color-grey">Call 13 39 38</a></p>
        )
    }

    return (
        <div className="booking-calendar d-flex">
            <div className="mr-30 booking-inner">
                <div className="mb-30">
                    <CustomCalendar
                        title="Select a new date"
                        availableDates={availableSlots}
                        handleChange={handleSetDate}
                    />
                </div>
                <div className="box booking-call">
                    <Help />
                </div>
            </div>
            {dateObj && (
                <div className="flex-grow-1">
                    <div className="box mb-30">
                        <CalendarIcon />
                        <div className="ml-2">
                            <p className="mt-1 mb-3 bold">Appointment date</p>
                            <p className="m-0 color-blue">{moment(dateObj.date).format('DD MMMM YYYY')}</p>
                        </div>
                    </div>
                    <div className="box mb-30">
                        <ClockIcon />
                        <div className="ml-2">
                            <p className="mt-1 mb-3 bold">Available time slot</p>
                            {dateObj.times.map((item, key) => {
                                return (
                                    <div
                                        key={key}
                                        className={`time-selection ${selectedTime.displayTime === item.displayTime && 'active'}`}
                                        onClick={() => setSelectedTime(item)}
                                    >
                                        {selectedTime.displayTime === item.displayTime ? <Checked /> : <Unchecked />}
                                        <div className="ml-2">
                                            {item.displayTime}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    <div className="text-center mb-30">
                        {isLoading ? <div className="py-4"><AjaxLoaderImg /></div> : (
                            <React.Fragment>
                                <Button action={handleConfirmAppointment}>Confirm appointment</Button>
                                <br /><br />
                                <button className="cancel" onClick={handleCancel}>Cancel</button>
                            </React.Fragment>
                        )}
                    </div>
                    {Object.keys(error).length > 0 && <Error errors={error} />}
                    <div className="d-lg-none mb-30">
                        <Help />
                    </div>
                </div>
            )}
        </div>
    )

}

export default BookingCalendar;

BookingCalendar.propTypes = {
    availableDates: PropTypes.arrayOf(PropTypes.shape({date: PropTypes.string, times: PropTypes.arrayOf(PropTypes.string)})).isRequired,
    handleCancel: PropTypes.func.isRequired
}

const CalendarIcon = () => {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15.0703 14.1751C15.3678 13.8351 15.3334 13.3182 14.9933 13.0206C14.6533 12.7231 14.1364 12.7575 13.8388 13.0976L11.5511 15.7122L10.5331 14.6942C10.2136 14.3747 9.69552 14.3747 9.376 14.6942C9.05648 15.0137 9.05648 15.5317 9.376 15.8513L11.0124 17.4876C11.1725 17.6478 11.3918 17.7344 11.6181 17.7268C11.8445 17.7193 12.0575 17.6183 12.2067 17.4479L15.0703 14.1751Z" fill="#004346"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M7.90909 3.81818C7.90909 3.36631 8.2754 3 8.72727 3C9.17914 3 9.54545 3.36631 9.54545 3.81818V4.63636H14.4545V3.81818C14.4545 3.36631 14.8209 3 15.2727 3C15.7246 3 16.0909 3.36631 16.0909 3.81818V4.63636H17.7273C19.5347 4.63636 21 6.10161 21 7.90909V17.7273C21 19.5347 19.5347 21 17.7273 21H6.27273C4.46525 21 3 19.5347 3 17.7273V7.90909C3 6.10161 4.46525 4.63636 6.27273 4.63636H7.90909V3.81818ZM14.4545 6.27273V7.09091C14.4545 7.54278 14.8209 7.90909 15.2727 7.90909C15.7246 7.90909 16.0909 7.54278 16.0909 7.09091V6.27273H17.7273C18.631 6.27273 19.3636 7.00535 19.3636 7.90909V9.54545H4.63636V7.90909C4.63636 7.00535 5.36899 6.27273 6.27273 6.27273H7.90909V7.09091C7.90909 7.54278 8.2754 7.90909 8.72727 7.90909C9.17914 7.90909 9.54545 7.54278 9.54545 7.09091V6.27273H14.4545ZM4.63636 11.1818H19.3636V17.7273C19.3636 18.631 18.631 19.3636 17.7273 19.3636H6.27273C5.36899 19.3636 4.63636 18.631 4.63636 17.7273V11.1818Z" fill="#004346"/>
        </svg>
    )
}

const ClockIcon = () => {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 7.45455C12.5021 7.45455 12.9091 7.86156 12.9091 8.36364V12.9091H15.6364C16.1384 12.9091 16.5455 13.3161 16.5455 13.8182C16.5455 14.3203 16.1384 14.7273 15.6364 14.7273H12C11.4979 14.7273 11.0909 14.3203 11.0909 13.8182V8.36364C11.0909 7.86156 11.4979 7.45455 12 7.45455Z" fill="#004346"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12 20.1818C16.5187 20.1818 20.1818 16.5187 20.1818 12C20.1818 7.48131 16.5187 3.81818 12 3.81818C7.48131 3.81818 3.81818 7.48131 3.81818 12C3.81818 16.5187 7.48131 20.1818 12 20.1818Z" fill="#004346"/>
        </svg>
    )
}

const Unchecked = () => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clipPath="url(#clip0_1199_725)">
                <circle cx="8" cy="8" r="7.5" fill="#F5F5F5" stroke="#CBCBCB"/>
            </g>
            <defs>
                <clipPath id="clip0_1199_725">
                    <rect width="16" height="16" fill="white"/>
                </clipPath>
            </defs>
        </svg>
    )
}

const Checked = () => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM11.8047 5.19526C12.0651 5.45561 12.0651 5.87772 11.8047 6.13807L7.13807 10.8047C6.87772 11.0651 6.45561 11.0651 6.19526 10.8047L4.19526 8.80474C3.93491 8.54439 3.93491 8.12228 4.19526 7.86193C4.45561 7.60158 4.87772 7.60158 5.13807 7.86193L6.66667 9.39052L10.8619 5.19526C11.1223 4.93491 11.5444 4.93491 11.8047 5.19526Z" fill="#54B758"/>
        </svg>
    )
}



// WEBPACK FOOTER //
// ./resources/js/components/BookingCalendar.js